import { Fragment, useState, type ReactNode } from 'react';
import {
  GIF_URL_RE,
  guessLanguage,
  languageLabel,
  SAFE_URL_RE,
  type PublicUser,
  type RichTextDoc,
} from '@avslack/shared';
import type { CustomEmoji } from '@avslack/shared';
import { useCustomEmoji } from '../data/custom-emoji';
import { useUsers } from '../data/users';
import { withCustomEmoji } from './Emoji';
import { usePrefersReducedMotion } from '../lib/motion';

interface AnyNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  content?: AnyNode[];
}

interface RichTextProps {
  doc: RichTextDoc;
  meId: string;
  usersById: Map<string, PublicUser>;
}

/**
 * Formazott uzenet megjelenitese.
 *
 * Sosem allitunk elo HTML-t szovegbol (nincs dangerouslySetInnerHTML): a
 * dokumentumot csomopontrol csomopontra React-elemekke alakitjuk. Amit a sema
 * nem ismer, azt egyszeruen nem rajzoljuk ki.
 */
export function RichText({ doc, meId, usersById }: RichTextProps) {
  const { byName } = useCustomEmoji();
  return (
    <>
      {(doc as AnyNode).content?.map((node, index) =>
        renderBlock(node, index, meId, usersById, byName),
      )}
    </>
  );
}

function renderBlock(
  node: AnyNode,
  key: number,
  meId: string,
  usersById: Map<string, PublicUser>,
  emojiByName: Map<string, CustomEmoji>,
): ReactNode {
  switch (node.type) {
    case 'paragraph':
      return (
        <p key={key} className="whitespace-pre-wrap break-words">
          {renderInline(node.content, meId, usersById, emojiByName)}
        </p>
      );

    case 'codeBlock':
      return (
        <CodeBlock
          key={key}
          language={node.attrs?.language as string | null | undefined}
          code={(node.content ?? []).map((child) => child.text ?? '').join('')}
        />
      );

    case 'gif':
      return <Gif key={key} node={node} />;

    case 'blockquote':
      return (
        <blockquote
          key={key}
          className="my-1.5 border-l-[3px] border-border-strong pl-3 text-ink-muted"
        >
          {node.content?.map((child, i) => renderBlock(child, i, meId, usersById, emojiByName))}
        </blockquote>
      );

    case 'bulletList':
      return (
        <ul key={key} className="my-1.5 list-disc space-y-0.5 pl-5">
          {renderListItems(node, meId, usersById, emojiByName)}
        </ul>
      );

    case 'orderedList':
      return (
        <ol
          key={key}
          start={Number(node.attrs?.start ?? 1)}
          className="my-1.5 list-decimal space-y-0.5 pl-5"
        >
          {renderListItems(node, meId, usersById, emojiByName)}
        </ol>
      );

    default:
      return null;
  }
}

function renderListItems(
  node: AnyNode,
  meId: string,
  usersById: Map<string, PublicUser>,
  emojiByName: Map<string, CustomEmoji>,
): ReactNode {
  return node.content?.map((item, i) => (
    <li key={i}>
      {item.content?.map((child, j) => renderBlock(child, j, meId, usersById, emojiByName))}
    </li>
  ));
}

function renderInline(
  nodes: AnyNode[] | undefined,
  meId: string,
  usersById: Map<string, PublicUser>,
  emojiByName: Map<string, CustomEmoji>,
): ReactNode {
  return (nodes ?? []).map((node, index) => {
    if (node.type === 'hardBreak') return <br key={index} />;

    if (node.type === 'mention') {
      return <MentionChip key={index} node={node} meId={meId} usersById={usersById} />;
    }

    if (node.type !== 'text') return null;
    return (
      <Fragment key={index}>{applyMarks(node.text ?? '', node.marks, emojiByName, index)}</Fragment>
    );
  });
}

function applyMarks(
  text: string,
  marks: { type: string; attrs?: Record<string, unknown> }[] | undefined,
  emojiByName: Map<string, CustomEmoji>,
  keyIndex: number,
): ReactNode {
  /**
   * Kodban NEM cserelunk emojit.
   *
   * Egy `:nev:` alaku reszlet a kodban jeloles, nem hangulatjel - ott a
   * pontos szoveg a lenyeg (pl. YAML-kulcs, TypeScript tipus).
   */
  const inCode = (marks ?? []).some((mark) => mark.type === 'code');
  let node: ReactNode = inCode ? text : withCustomEmoji(text, emojiByName, `t${keyIndex}`);

  for (const mark of marks ?? []) {
    switch (mark.type) {
      case 'bold':
        node = <strong className="font-semibold">{node}</strong>;
        break;
      case 'italic':
        node = <em>{node}</em>;
        break;
      case 'underline':
        node = <u className="underline underline-offset-2">{node}</u>;
        break;
      case 'strike':
        node = <s className="text-ink-muted">{node}</s>;
        break;
      case 'code':
        node = (
          <code
            translate="no"
            className="rounded border border-border-soft bg-surface-sunken px-1 py-px font-mono text-[0.9em]"
          >
            {node}
          </code>
        );
        break;
      case 'link': {
        const href = String(mark.attrs?.href ?? '');
        // Masodik vedvonal: a sema mar kiszurte, de a megjelenites sem bizik benne.
        if (!SAFE_URL_RE.test(href)) break;
        node = (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-accent underline underline-offset-2 hover:text-accent-hover"
          >
            {node}
          </a>
        );
        break;
      }
      default:
        break;
    }
  }

  return node;
}

/**
 * GIF az uzenetben.
 *
 * A cimet a sema mar szurte (csak Giphy), de itt is ellenorizzuk - a
 * megjelenites a masodik vedvonal: egy regi, mas szabalyokkal mentett
 * dokumentum sem kuldhet kerest idegen szerverre.
 *
 * A `width`/`height` megadasa nem kozmetikai: enelkul a kep betoltodesekor
 * ugrana a beszelgetes, es ki tudna rantani a szem alol a sort, amit eppen
 * olvasunk.
 */
function Gif({ node }: { node: AnyNode }) {
  const reduceMotion = usePrefersReducedMotion();
  const animated = String(node.attrs?.url ?? '');
  const still = node.attrs?.stillUrl ? String(node.attrs.stillUrl) : null;
  // Csokkentett mozgasnal az allo kocka - ha a (regebbi) uzenetben nincs, marad az animalt.
  const url = reduceMotion && still && GIF_URL_RE.test(still) ? still : animated;
  const width = Number(node.attrs?.width ?? 0);
  const height = Number(node.attrs?.height ?? 0);
  const title = node.attrs?.title ? String(node.attrs.title) : 'GIF';

  if (!GIF_URL_RE.test(animated)) return null;

  return (
    <img
      src={url}
      alt={title}
      title={title}
      width={width || undefined}
      height={height || undefined}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="my-1.5 h-auto w-full max-w-sm rounded-control border border-border-soft"
    />
  );
}

/**
 * Kodblokk.
 *
 * Kiemelt doboz, nyelv-cimkevel es masolas gombbal. A masolas azert van jobb
 * felul es azert jelenik meg raviteltre, mert a kod SZELESSEGE a lenyeges: egy
 * allando gomb elvenne a helyet a hosszu soroktol, viszont a kod kimasolasa a
 * leggyakoribb muvelet, amit egy kodreszlettel csinalunk.
 */
function CodeBlock({ language, code }: { language: string | null | undefined; code: string }) {
  const [copied, setCopied] = useState(false);
  const lines = code.split('\n').length;

  /**
   * Nyelv nelkuli blokknal MEGJELENITESKOR tippelunk.
   *
   * A beillesztett kod a beszuraskor kapja a cimket, de aki kezzel nyit egy
   * kodblokkot, annak nincs. Igy a cimke a szerkesztest is koveti, nem ragad
   * be egy regi tipp.
   */
  const shown = language ?? guessLanguage(code);

  const copy = () => {
    void navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <div className="group/code relative my-1.5 overflow-hidden rounded-control border border-border-soft bg-surface-sunken">
      <div className="flex items-center justify-between border-b border-border-soft px-3 py-1">
        <span className="font-mono text-2xs uppercase tracking-wide text-ink-faint">
          {languageLabel(shown)}
          {lines > 1 && ` · ${lines} sor`}
        </span>

        <button
          type="button"
          onClick={copy}
          aria-label="Kód másolása"
          className={[
            'rounded-control px-2 py-0.5 text-2xs font-medium transition-[opacity,background-color,color] duration-(--duration-quick)',
            // Erintokepernyon nincs raviteltes: ott mindig latszik.
            'opacity-0 group-hover/code:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
            copied
              ? 'bg-accent-soft text-accent'
              : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
          ].join(' ')}
        >
          {copied ? 'Másolva ✓' : 'Másolás'}
        </button>
        {/* A gomb aria-labelje elfedi a felirat valtozasat - a visszajelzes KULON, mindig jelen levo live regiobol szol. */}
        <span role="status" className="sr-only">
          {copied ? 'Kód másolva' : ''}
        </span>
      </div>

      <pre translate="no" className="overflow-x-auto px-3 py-2 font-mono text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * Emlites-jelvény.
 *
 * Ha ENGEM emlitenek, figyelem-szint kap (terrakotta) - ez az egyetlen hely az
 * uzenetfolyamban, ahol ez a szin megjelenik, ezert azonnal kiszurja a szem.
 * A @channel es a @here mindig kiemelt, mert mindenkinek szol.
 */
function MentionChip({
  node,
  meId,
  usersById,
}: {
  node: AnyNode;
  meId: string;
  usersById: Map<string, PublicUser>;
}) {
  const kind = String(node.attrs?.kind ?? 'user');
  const id = String(node.attrs?.id ?? '');
  const fallback = String(node.attrs?.label ?? id);

  // A nézői beállítás szerint: becenév mód esetén a becenév (ahol van).
  const { nameOf } = useUsers();
  const label = kind === 'user' && usersById.has(id) ? nameOf(id, fallback) : fallback;
  // A csoport-emlites nem szemelyes cimzes, ezert halk marad; a @channel es a
  // @here viszont mindenkinek szol, azok kiemeltek.
  const addressesMe = kind === 'channel' || kind === 'here' || (kind === 'user' && id === meId);

  return (
    <span
      className={[
        'rounded px-1 py-px font-semibold',
        addressesMe ? 'bg-alert-soft text-alert' : 'bg-accent-soft text-accent',
      ].join(' ')}
      title={
        kind === 'channel'
          ? 'A csatorna minden tagja'
          : kind === 'here'
            ? 'Az éppen elérhetők'
            : label
      }
    >
      @{label}
    </span>
  );
}
